/**
 * Shop The Look (new) — <stl-lookbook> and <stl-bundle-drawer>.
 *
 * This file is a deliberate, self-contained port of four originals:
 *   assets/looks-grid.js        (<looks-filter>: category filter, priority sort)
 *   assets/featured-lookbook.js (shuffle, hard cap)
 *   assets/product-slider.js    (<product-content-slider>: arrows, progress)
 *   assets/look-bundle-popup.js (<look-bundle-popup>: the "+" drawer)
 *
 * It shares no element name, attribute, event name or class with any of them,
 * and it is never loaded by them. That is a requirement, not a preference:
 * Featured Lookbook and this section sit on the same homepage, and three of
 * those originals reach across the document.
 *
 *   - look-bundle-popup binds a click listener to `document` for
 *     [data-look-bundle-trigger]. Reusing that attribute would make the old
 *     drawer open for these cards.
 *   - look-bundle-popup removes any second <look-bundle-popup> on the page, so
 *     reusing the element name would make one drawer delete the other.
 *   - looks-filter re-dispatches `looks:swapped` / `looks:loaded` on `document`
 *     for cross-section listeners, so reusing those names would have each
 *     section answering the other's filtering.
 *   - product-content-slider is shared by nine sections; duplicating its arrow
 *     and progress logic here is far cheaper than risking all nine.
 *
 * One deliberate behaviour difference from the original, described in the
 * section's settings: category state is kept in the element, not in the page
 * URL. The original writes `?category=` and listens to popstate, which two
 * lookbooks on one page would fight over. It is available as an opt-in
 * (data-stl-url-state="1") and is off by default.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * <stl-lookbook> — the rail, its filtering, ordering and arrows.
   * ------------------------------------------------------------------ */

  if (!customElements.get('stl-lookbook')) {
    class StlLookbook extends HTMLElement {
      constructor() {
        super();
        this._onFilterClick = this._onFilterClick.bind(this);
        this._onPopState = this._onPopState.bind(this);
        this._onTrackScroll = this._onTrackScroll.bind(this);
        this._onResize = this._onResize.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
      }

      connectedCallback() {
        this.track = this.querySelector('[data-stl-track]');
        if (!this.track) return;

        this.prevBtn = this.querySelector('[data-stl-prev]');
        this.nextBtn = this.querySelector('[data-stl-next]');
        this.emptyEl = this.querySelector('[data-stl-empty]');
        this.progressEl = this.querySelector('[data-stl-progress]');
        this.progressThumb = this.querySelector('[data-stl-progress-thumb]');
        this.filterButtons = Array.from(this.querySelectorAll('[data-stl-filter]'));

        this.source = this.dataset.stlSource || 'manual';
        this.maxItems = parseInt(this.dataset.stlMaxItems || '0', 10) || 0;
        this.useUrlState = this.dataset.stlUrlState === '1';

        this.allCards = Array.from(this.track.querySelectorAll('[data-stl-card]'));
        this._indexCardCategories();

        /* Order, in the same sequence the originals apply it: shuffle first (it
           is a DOM reorder), then priority sort, then filter, then the cap. */
        if (this.source === 'random') this._shuffle();
        if (this.source === 'by_priority' || this.source === 'category') this._sortByPriority();

        this.activeCategory = this.useUrlState ? this._readCategoryFromUrl() : (this.dataset.stlDefaultCategory || '');

        this._bindEvents();
        this._applyFilter(this.activeCategory, false);

        /* Releases the anti-flash guard: the rail is hidden until ordered, so a
           shopper never sees the pre-shuffle order rearrange itself. */
        this.setAttribute('data-stl-ready', '');
        this._updateArrows();
        this._updateProgress();
      }

      disconnectedCallback() {
        this.filterButtons.forEach((b) => b.removeEventListener('click', this._onFilterClick));
        if (this.prevBtn) this.prevBtn.removeEventListener('click', this._prevHandler);
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this._nextHandler);
        if (this.track) this.track.removeEventListener('scroll', this._onTrackScroll);
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('popstate', this._onPopState);
        this.removeEventListener('keydown', this._onKeydown);
      }

      _bindEvents() {
        this.filterButtons.forEach((b) => b.addEventListener('click', this._onFilterClick));

        this._prevHandler = () => this._scrollByPage(-1);
        this._nextHandler = () => this._scrollByPage(1);
        if (this.prevBtn) this.prevBtn.addEventListener('click', this._prevHandler);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this._nextHandler);

        this.track.addEventListener('scroll', this._onTrackScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });
        this.addEventListener('keydown', this._onKeydown);

        if (this.useUrlState) window.addEventListener('popstate', this._onPopState);
      }

      /* ---- ordering ---- */

      /* Fisher-Yates, as in featured-lookbook.js. */
      _shuffle() {
        const cards = this.allCards.slice();
        for (let i = cards.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          const t = cards[i];
          cards[i] = cards[j];
          cards[j] = t;
        }
        cards.forEach((card) => this.track.appendChild(card));
        this.allCards = cards;
      }

      /* Higher priority first. Array.prototype.sort is stable, so equal
         priorities keep the metaobject's own order. */
      _sortByPriority() {
        const priorityOf = (card) => parseInt(card.dataset.priority || '0', 10) || 0;
        this.allCards.sort((a, b) => priorityOf(b) - priorityOf(a));
        this.allCards.forEach((card) => this.track.appendChild(card));
      }

      _indexCardCategories() {
        this.allCards.forEach((card) => {
          const raw = (card.dataset.categories || '').toLowerCase();
          card._stlCategories = new Set(
            raw.split(',').map((s) => s.trim()).filter(Boolean)
          );
        });
      }

      /* ---- filtering ---- */

      _readCategoryFromUrl() {
        try {
          return (new URLSearchParams(window.location.search).get('category') || '').toLowerCase();
        } catch (_) {
          return '';
        }
      }

      _writeCategoryToUrl(category) {
        try {
          const url = new URL(window.location.href);
          if (category) url.searchParams.set('category', category);
          else url.searchParams.delete('category');
          window.history.pushState({ stlCategory: category }, '', url.toString());
        } catch (_) { /* history unavailable — filtering still works */ }
      }

      _onPopState() {
        this._applyFilter(this._readCategoryFromUrl(), false);
      }

      _onFilterClick(event) {
        const btn = event.currentTarget;
        const category = (btn.dataset.stlFilter || '').toLowerCase();
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
        event.preventDefault();
        if (category === this.activeCategory) return;
        this._applyFilter(category, true);
      }

      _applyFilter(category, pushState) {
        this.activeCategory = category;

        this.filterButtons.forEach((btn) => {
          const isActive = (btn.dataset.stlFilter || '').toLowerCase() === category;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        const matching = category
          ? this.allCards.filter((card) => card._stlCategories.has(category))
          : this.allCards.slice();

        /* The cap is applied to the matching set, so every tab gets up to N
           cards rather than sharing one global budget. */
        this.allCards.forEach((card) => { card.hidden = true; });
        const limit = this.maxItems > 0 ? Math.min(this.maxItems, matching.length) : matching.length;
        for (let i = 0; i < limit; i++) matching[i].hidden = false;

        this.visibleCount = limit;
        if (this.emptyEl) this.emptyEl.hidden = matching.length > 0;

        if (pushState && this.useUrlState) this._writeCategoryToUrl(category);

        /* A new set starts from the beginning rather than at the previous
           tab's scroll offset. Instant, because the swap itself is instant. */
        this.track.scrollLeft = 0;
        this._updateArrows();
        this._updateProgress();

        /* Scoped to this element on purpose — the original also re-dispatches
           on document, which is exactly the cross-talk this section avoids. */
        this.dispatchEvent(new CustomEvent('stl:swapped', {
          detail: { category: category, matched: matching.length, shown: limit }
        }));
      }

      /* ---- rail ---- */

      _scrollByPage(direction) {
        this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: 'smooth' });
      }

      _onKeydown(event) {
        if (event.target.closest('[data-stl-card]') == null && event.target !== this.track) return;
        if (event.key === 'ArrowLeft') this._scrollByPage(-1);
        else if (event.key === 'ArrowRight') this._scrollByPage(1);
      }

      _onTrackScroll() {
        if (this._scrollTick) return;
        this._scrollTick = true;
        requestAnimationFrame(() => {
          this._scrollTick = false;
          this._updateArrows();
          this._updateProgress();
        });
      }

      _onResize() {
        this._updateArrows();
        this._updateProgress();
      }

      _updateArrows() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        /* A 2px slack absorbs sub-pixel scroll positions, which otherwise leave
           the end arrow enabled on a rail that cannot move. */
        const atStart = this.track.scrollLeft <= 2;
        const atEnd = this.track.scrollLeft >= max - 2;
        const noOverflow = max <= 2;

        if (this.prevBtn) this.prevBtn.disabled = noOverflow || atStart;
        if (this.nextBtn) this.nextBtn.disabled = noOverflow || atEnd;
        this.toggleAttribute('data-stl-no-overflow', noOverflow);
      }

      _updateProgress() {
        if (!this.progressEl || !this.progressThumb) return;
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (max <= 2) {
          this.progressEl.hidden = true;
          return;
        }
        this.progressEl.hidden = false;
        const ratio = this.track.clientWidth / this.track.scrollWidth;
        const pos = this.track.scrollLeft / max;
        this.progressThumb.style.width = (ratio * 100).toFixed(2) + '%';
        this.progressThumb.style.transform = 'translateX(' + (pos * (100 / ratio - 100)).toFixed(2) + '%)';
      }
    }

    customElements.define('stl-lookbook', StlLookbook);
  }

  /* ------------------------------------------------------------------ *
   * <stl-bundle-drawer> — the "+" drawer.
   * ------------------------------------------------------------------ */

  if (!customElements.get('stl-bundle-drawer')) {
    class StlBundleDrawer extends HTMLElement {
      constructor() {
        super();
        this._onTriggerClick = this._onTriggerClick.bind(this);
        this._onDialogClick = this._onDialogClick.bind(this);
        this._onClose = this._onClose.bind(this);
        this._cache = new Map();
      }

      connectedCallback() {
        /* Deduplicate against other copies of THIS drawer only. The original's
           dedup looks for look-bundle-popup, so the two never see each other. */
        const existing = document.querySelector('stl-bundle-drawer[data-stl-mounted]');
        if (existing && existing !== this) {
          this.remove();
          return;
        }
        this.setAttribute('data-stl-mounted', '');

        this.dialog = this.querySelector('[data-stl-dialog]');
        this.titleEl = this.querySelector('[data-stl-title]');
        this.bodyEl = this.querySelector('[data-stl-body]');
        this.viewLinkEl = this.querySelector('[data-stl-view-link]');
        this.footerEl = this.querySelector('[data-stl-footer]');
        this.innerEl = this.querySelector('[data-stl-inner]');
        this.closeBtn = this.querySelector('[data-stl-close]');

        if (!this.dialog) return;

        document.addEventListener('click', this._onTriggerClick);
        this.dialog.addEventListener('click', this._onDialogClick);
        this.dialog.addEventListener('close', this._onClose);
        if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
        /* Clicks inside the panel must not read as backdrop clicks. */
        if (this.innerEl) this.innerEl.addEventListener('click', (e) => e.stopPropagation());

        this.removeAttribute('hidden');
      }

      disconnectedCallback() {
        document.removeEventListener('click', this._onTriggerClick);
        if (this.dialog) {
          this.dialog.removeEventListener('click', this._onDialogClick);
          this.dialog.removeEventListener('close', this._onClose);
        }
      }

      _onTriggerClick(event) {
        const trigger = event.target.closest('[data-stl-bundle-trigger]');
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        this.openFromTrigger(trigger);
      }

      openFromTrigger(trigger) {
        const handle = trigger.dataset.lookHandle || '';
        const title = trigger.dataset.lookTitle || '';
        const url = trigger.dataset.lookUrl || '';

        if (this.titleEl) this.titleEl.textContent = title;
        if (this.viewLinkEl) {
          const hasUrl = !!url;
          if (hasUrl) this.viewLinkEl.setAttribute('href', url);
          this.viewLinkEl.hidden = !hasUrl;
          if (this.footerEl) this.footerEl.hidden = !hasUrl;
        }

        /* Open first so the drawer feels immediate; the body fills in after. */
        this.open();
        this._loadBundle(handle, url, trigger);
      }

      _loadBundle(handle, url, trigger) {
        if (!this.bodyEl) return;

        /* Guards against out-of-order responses when a shopper opens one look
           and quickly opens another: only the newest request may paint. */
        const reqId = (this._reqId = (this._reqId || 0) + 1);
        const isStale = () => reqId !== this._reqId;

        const cached = handle && this._cache.get(handle);
        if (cached != null) {
          this.bodyEl.innerHTML = cached;
          if (typeof window.resetKlaviyoBIS === 'function') window.resetKlaviyoBIS();
          return;
        }

        if (!url) {
          this._renderError();
          return;
        }

        this.bodyEl.innerHTML = this._loadingMarkup();
        if (trigger) trigger.setAttribute('aria-busy', 'true');

        const endpoint = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=stl-bundle-content';

        fetch(endpoint, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })
          .then((html) => {
            if (isStale()) return;
            const markup = this._extract(html);
            if (handle) this._cache.set(handle, markup);
            this.bodyEl.innerHTML = markup;
            if (typeof window.resetKlaviyoBIS === 'function') window.resetKlaviyoBIS();
          })
          .catch(() => {
            if (isStale()) return;
            this._renderError();
          })
          .finally(() => {
            if (trigger) trigger.removeAttribute('aria-busy');
          });
      }

      /* Section Rendering wraps the cards in the section's own div. Unwrapping
         keeps the cards as direct children of the flex body, so the column gap
         applies. */
      _extract(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('.shopify-section');
        return (section ? section.innerHTML : doc.body.innerHTML).trim();
      }

      _loadingMarkup() {
        const label = (window.themeStrings && window.themeStrings.loading) || 'Loading';
        return (
          '<div class="stl-drawer__loading" role="status" aria-live="polite">' +
          '<span class="stl-drawer__spinner" aria-hidden="true"></span>' +
          '<span class="visually-hidden">' + label + '</span></div>'
        );
      }

      _renderError() {
        if (!this.bodyEl) return;
        const msg = (window.themeStrings && window.themeStrings.bundleError) ||
          "We couldn't load this look right now. Please try again or view the full look below.";
        this.bodyEl.innerHTML = '<p class="stl-drawer__error" role="alert">' + msg + '</p>';
      }

      open() {
        if (!this.dialog) return;
        if (typeof this.dialog.showModal === 'function' && !this.dialog.open) {
          /* showModal gives a focus trap and escape-to-close for free. */
          this.dialog.showModal();
        } else if (!this.dialog.open) {
          this.dialog.setAttribute('open', '');
        }
        document.body.classList.add('modal-open', 'stl-drawer-open');
        this.dispatchEvent(new CustomEvent('stl-bundle:opened', { bubbles: true }));
      }

      close() {
        if (!this.dialog) return;
        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this._onClose();
        }
      }

      _onDialogClick(event) {
        /* Klaviyo's back-in-stock trigger is appended into the cards after they
           render, so it cannot be bound directly. Delegate instead: close this
           drawer so Klaviyo's own modal is not stranded under the top layer,
           but do not stop propagation — Klaviyo's listener lives on <body> and
           needs this same click. Deferring a frame lets it read the trigger
           while it is still attached. */
        if (event.target.closest('.klaviyo-bis-trigger')) {
          requestAnimationFrame(() => this.close());
          return;
        }
        if (event.target === this.dialog) this.close();
      }

      _onClose() {
        document.body.classList.remove('modal-open', 'stl-drawer-open');
        if (this.bodyEl) this.bodyEl.replaceChildren();
        this.dispatchEvent(new CustomEvent('stl-bundle:closed', { bubbles: true }));
      }
    }

    customElements.define('stl-bundle-drawer', StlBundleDrawer);
  }

  /* ------------------------------------------------------------------ *
   * <stl-item> — one product row inside the drawer.
   *
   * <product-form> already has variant-change handling, but it only binds
   * when it finds `select.product-variant-id`, and it writes the new price
   * into `.product-card__price-current`. Both of those are classes the
   * store-wide horizontal card owns and that 0-product-info.css and
   * product-card-horizontal.css style — and Featured Lookbook's drawer and
   * the cart drawer load those two stylesheets on this same homepage. Using
   * them here would mean this drawer could never be restyled without moving
   * the other one too, which is the whole point of the section.
   *
   * So the row keeps its own class names and does the three things that
   * depend on them itself: price text, compare-at and badge, and the
   * add-versus-notify-me swap when a sold-out size is chosen.
   * <product-form> is left to do the part that is genuinely shared — the
   * add-to-cart request and everything the cart listens for.
   * ------------------------------------------------------------------ */
  if (!customElements.get('stl-item')) {
    class StlItem extends HTMLElement {
      constructor() {
        super();
        this._onChange = this._onChange.bind(this);
      }

      connectedCallback() {
        this.select = this.querySelector('[data-stl-item-select]');
        this.priceEl = this.querySelector('[data-stl-item-price]');
        this.compareEl = this.querySelector('[data-stl-item-compare]');
        this.badgeEl = this.querySelector('[data-stl-item-badge]');
        this.addBtn = this.querySelector('[data-stl-item-add]');
        this.notifyEl = this.querySelector('[data-stl-item-notify]');

        this.variants = [];
        const json = this.querySelector('.stl-item__variants');
        if (json) {
          try {
            this.variants = JSON.parse(json.textContent).variants || [];
          } catch (e) {
            /* A malformed payload must not take the add-to-cart down with it:
               without variant data the row simply stops updating its price,
               and the select still posts a valid variant id. */
            this.variants = [];
          }
        }

        if (!this.select) return;
        this.select.addEventListener('change', this._onChange);

        /* The row is injected into an open drawer, so nothing else will
           synchronise it. Run once for the size the select landed on. */
        this._sync();
      }

      disconnectedCallback() {
        if (this.select) this.select.removeEventListener('change', this._onChange);
      }

      _onChange() {
        this._sync();
      }

      _sync() {
        const variant = this.variants.find(
          (v) => String(v.id) === String(this.select.value)
        );
        if (!variant) return;

        if (this.priceEl) this.priceEl.textContent = variant.price;

        /* compare_at_price is only a strikethrough when it is actually higher.
           Shopify returns the raw field, which is often equal to the price on
           products that were once on sale. */
        const onSale = variant.compare_at_cents > variant.price_cents;
        if (this.compareEl) {
          this.compareEl.textContent = variant.compare_at_price;
          this.compareEl.hidden = !onSale;
        }
        if (this.badgeEl) {
          this.badgeEl.hidden = !onSale;
          if (onSale) {
            const off = Math.floor(
              ((variant.compare_at_cents - variant.price_cents) * 100) / variant.compare_at_cents
            );
            /* Same rule as snippets/final-sale-check.liquid (Theme settings →
               Final sale), so a size change cannot disagree with the badge the
               row first rendered. A threshold of 0 turns the percent rule off. */
            const threshold = parseInt(this.dataset.finalSalePercent, 10) || 0;
            const finalSale =
              this.hasAttribute('data-final-sale-tag') || (threshold > 0 && off >= threshold);
            const type = finalSale ? 'final_sale' : 'sale';
            this.badgeEl.classList.toggle('stl-item__badge--sale', type === 'sale');
            this.badgeEl.classList.toggle('stl-item__badge--final_sale', type === 'final_sale');
          }
        }

        /* A sold-out size hides the add button and reveals Klaviyo's
           back-in-stock container. The class is the app's, not the theme's:
           Klaviyo's onsite script finds its targets by .klaviyo-product-container
           and will not inject into anything renamed. */
        const unavailable = variant.available === false;
        if (this.addBtn) this.addBtn.hidden = unavailable;
        if (this.notifyEl) this.notifyEl.classList.toggle('hidden', !unavailable);
      }
    }

    customElements.define('stl-item', StlItem);
  }
})();
